import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cleanNotifications, showNotification } from "@mantine/notifications";
import { CampaignReviewsQuery, ReviewCommentsQuery, ReviewState } from "src/graphql/generated/graphql";
import { approveReviewMutation, sendReviewCommentMutation } from "src/graphql/operations/reviews/mutations";
import { campaignReviewsQuery, reviewCommentsQuery } from "src/graphql/operations/reviews/query";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";

import { handleError } from "../utils/error";
import { useAuthContext } from "./auth";

export const ReviewDestinationType = {
    tt: "tt",
    heureka: "heureka",
    mall: "mall",
    custom: "custom",
} as const;

export type ReviewDestinationType = (typeof ReviewDestinationType)[keyof typeof ReviewDestinationType];

export interface CampaignReviewsOptions {
    campaignId: string;
    productId: string;
    nextToken?: string;
    limit?: number;
    state?: ReviewState[];
}

export const useCampaignReviews = (options: CampaignReviewsOptions) => {
    const [reviews, setReviews] = useState<CampaignReviewsQuery["campaign"]["reviews"]["items"]>([]);
    const [isLoadingReviews, setIsLoadingReviews] = useState(false);

    const fetchReviews = useCallback(async (options: CampaignReviewsOptions, nextToken?: string) => {
        setIsLoadingReviews(true);

        let hasNext = false;
        try {
            const response = await graphqlFetch(campaignReviewsQuery, { ...options, nextToken });

            if (response.campaign?.reviews?.items) {
                setReviews((prevReviews) => [...prevReviews, ...response?.campaign?.reviews?.items]);
            }

            if (response.campaign?.reviews?.nextToken) {
                hasNext = true;
                fetchReviews(options, response.campaign.reviews.nextToken);
            }
        } catch (err) {
            const { state, ...rest } = options;
            handleError(err, { silent: true, context: { ...rest, state: state?.join(",") } });
        } finally {
            if (!hasNext) {
                setIsLoadingReviews(false);
            }
        }
    }, []);

    useEffect(() => {
        if (!options.campaignId || !options.productId) {
            return;
        }

        setReviews([]);
        fetchReviews(options);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(options)]);

    return {
        reviews,
        isLoadingReviews,
        reloadReviews: fetchReviews,
    };
};

export type ReviewCommentsQueryDataType = ReviewCommentsQuery["review"]["comments"]["items"][0];

export const useReviewComments = (reviewId: string, shouldIgnore = false) => {
    const { client } = useAuthContext();

    const [reviewComments, setReviewComments] = useState<ReviewCommentsQueryDataType[]>([]);
    const [isLoadingReviewComments, setIsLoadingReviewComments] = useState(false);

    const fetchReviewComments = useCallback(
        async (reviewId: string) => {
            setIsLoadingReviewComments(true);

            try {
                const response = await graphqlFetch(reviewCommentsQuery, { clientId: client?.id, reviewId });

                if (response.review?.comments?.items?.length) {
                    setReviewComments((reviewComments) => [...reviewComments, ...response?.review?.comments?.items]);
                }
            } catch (err) {
                handleError(err, { context: { reviewId } });
            } finally {
                setIsLoadingReviewComments(false);
            }
        },
        [client?.id]
    );

    useEffect(() => {
        if (!client?.id || !reviewId || shouldIgnore) {
            return;
        }

        setReviewComments([]);
        fetchReviewComments(reviewId);
    }, [client?.id, fetchReviewComments, reviewId, shouldIgnore]);

    return {
        reviewComments,
        isLoadingReviewComments,
        reloadReviewComments: useCallback(() => {
            if (!client?.id || !reviewId) {
                return;
            }

            setReviewComments([]);
            fetchReviewComments(reviewId);
        }, [client?.id, fetchReviewComments, reviewId]),
    };
};

export const useApproveReview = (reviewId: string) => {
    const { t } = useTranslation(["common"]);

    const { client } = useAuthContext();

    const approveReview = useCallback(async () => {
        showNotification({
            color: "blue",
            message: t("review_approving"),
            loading: true,
        });

        try {
            const response = await graphqlFetch(approveReviewMutation, { clientId: client?.id, reviewId });

            return response.approveReview;
        } catch (err) {
            handleError(err, {
                context: { reviewId },
                message: { key: "review_approving_failed", namespace: "common" },
            });
        }
    }, [t, client?.id, reviewId]);

    return {
        approveReview,
    };
};

export const useSendReviewComment = (reviewId: string) => {
    const { t } = useTranslation(["common"]);

    const { client } = useAuthContext();

    const sendReviewComment = useCallback(
        async (content: string) => {
            showNotification({
                color: "blue",
                message: t("review_comment_sending"),
                loading: true,
            });

            try {
                const response = await graphqlFetch(sendReviewCommentMutation, {
                    clientId: client?.id,
                    reviewId,
                    content,
                });

                return response.sendReviewComment;
            } catch (err) {
                handleError(err, { context: { reviewId } });
            }
        },
        [t, client?.id, reviewId]
    );

    return {
        sendReviewComment,
    };
};
